import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  takeUntil,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { httpOptions } from '../_helpers/utils';
import { ExperimentTemplateListItem } from '../_models/experiment-template-list-item';
import { PaginationData } from '../_models/pagination-data.model';
import { ChangelogEntry } from '../_models/changelog';
import { ChangelogResponse } from '../_models/api-responses';
import { AuthenticationService } from './authentication.service';
import {
  ExperimentTemplate,
  ExperimentTemplateIndexResponse,
  ExperimentTemplateNames,
} from '../_models/experiment-template';
import { Laboratory } from '../_models/laboratory';

@Injectable({
  providedIn: 'root',
})
export class ExperimentTemplateService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  httpOptions = httpOptions;
  currentLaboratoryId: number | null = null;

  private readonly unsavedParametersCache$ =
    new BehaviorSubject<ExperimentTemplate | null>(null);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (x: Laboratory | null) => {
          this.currentLaboratoryId = x?.laboratoryId ?? null;
        },
        error: (error: unknown) => {
          this.currentLaboratoryId = null;
        },
      });
  }

  cacheUnsavedParameters = (formValues: ExperimentTemplate): void => {
    this.unsavedParametersCache$.next(formValues);
    sessionStorage.setItem(
      'unsavedParametersCache',
      JSON.stringify(formValues),
    );
  };

  public retrieveAndClearUnsavedCache(): ExperimentTemplate | null {
    const cached = this.unsavedParametersCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.unsavedParametersCache$.next(null);
    sessionStorage.removeItem('unsavedParametersCache');
  }

  // HTTP GET /EXPERIMENT-TEMPLATE
  getExperimentTemplateById(id: number): Observable<ExperimentTemplate> {
    return this.http
      .get<ExperimentTemplate>(
        `${environment.apiUrl}/experiment-templates/${id}`,
      )
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  getExperimentTemplates(
    laboratoryId: number,
    sortBy = 'Name',
    sortOrder = 'asc',
    pageSize = 5,
    pageNumber = 1,
    experimentMode: number = null,
    experimentModules: number[] = null,
    search: string = null,
    includeDeleted = false,
  ): Observable<ExperimentTemplateIndexResponse> {
    let url = `${environment.apiUrl}/experiment-templates?LaboratoryId=${laboratoryId}&OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}&IncludeDeleted=${includeDeleted}`;

    if (experimentMode !== null) {
      url += `&ExperimentMode=${experimentMode}`;
    }
    if (experimentModules?.length) {
      experimentModules.forEach((m) => (url += `&Modules=${m}`));
    }
    if (search) {
      url += `&Search=${search}`;
    }

    return this.http
      .get<ExperimentTemplateListItem[]>(url, this.httpOptions)
      .pipe(
        map((response) => {
          const headerValue = response.headers.get('X-Pagination');
          let paginationData: PaginationData | null = null;
          if (headerValue) {
            try {
              paginationData = JSON.parse(headerValue) as PaginationData;
            } catch {
              paginationData = null;
            }
          }
          const templates = response.body;
          return { paginationData, templates };
        }),
      );
  }

  getExperimentTemplatesForDropdown(
    laboratoryId: number,
  ): Observable<ExperimentTemplateNames[]> {
    const url = `${environment.apiUrl}/experiment-templates/names?laboratoryId=${laboratoryId}`;

    return this.http
      .get<ExperimentTemplateNames[]>(url, this.httpOptions)
      .pipe(map((response) => response.body ?? []));
  }

  // HTTP POST /EXPERIMENT
  createExperimentTemplate(
    newExperimentTemplate: ExperimentTemplate,
  ): Observable<ExperimentTemplate> {
    if (this.currentLaboratoryId === null) {
      return throwError(() => ({
        status: -1,
        message:
          'No laboratory selected. Please select a laboratory and try again.',
        code: null,
      }));
    }
    newExperimentTemplate.laboratoryId = this.currentLaboratoryId;
    return this.http
      .post<ExperimentTemplate>(
        `${environment.apiUrl}/experiment-templates`,
        newExperimentTemplate,
      )
      .pipe(
        map((response: ExperimentTemplate) => {
          return response;
        }),
      );
  }

  // HTTP PATCH /EXPERIMENT-TEMPLATE
  updateExperimentTemplate(
    template: ExperimentTemplate,
  ): Observable<ExperimentTemplate> {
    return this.http
      .patch<ExperimentTemplate>(
        `${environment.apiUrl}/experiment-templates/${template.id}`,
        template,
      )
      .pipe(map((response: ExperimentTemplate) => response));
  }

  // HTTP GET /CHANGELOG
  getExperimentTemplateChangelog(
    id: number,
    sortBy = 'CreatedAt',
    sortOrder = 'asc',
    pageSize = 10,
    pageNumber = 1,
  ): Observable<ChangelogResponse> {
    return this.http
      .get<
        ChangelogEntry[]
      >(`${environment.apiUrl}/change-logs/experiment-templates/${id}?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}`, this.httpOptions)
      .pipe(
        map((response) => {
          const headerValue = response.headers.get('X-Pagination');
          let paginationData: PaginationData | null = null;
          if (headerValue) {
            try {
              paginationData = JSON.parse(headerValue) as PaginationData;
            } catch {
              paginationData = null;
            }
          }
          return { changelogList: response.body, paginationData };
        }),
      );
  }

  deleteTemplate(templateId: number): Observable<unknown> {
    return this.http.delete<unknown>(
      `${environment.apiUrl}/experiment-templates/${templateId}`,
      this.httpOptions,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
